import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Schema } from '../../../../core/_models/schema';
import { SchemaTranslations } from '../../../../shared/models/schema-translations';
import { AbstractControlValueAccessor } from '../../../../shared/abstract_control_value_accessor.abstract';
import { Subscription } from 'rxjs';
import { IdentifierType } from '@app/core/_models/member-interface';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';
import { NgClass } from '@angular/common';

type InputType = 'number' | 'email' | 'text' | 'checkbox';

@Component({
  selector: 'app-property-normal-input',
  templateUrl: './property-normal-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PropertyNormalInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PropertyNormalInputComponent), multi: true },
  ],
  imports: [
    ReactiveFormsModule,
    AppFocusableDirective,
    NgClass,
  ],
})
export class PropertyNormalInputComponent extends AbstractControlValueAccessor implements OnInit, OnDestroy, Validator {
  public readonly AUTOCOMPLETE = {
    first_name: 'given-name',
    last_name: 'family-name',
    email: 'email',
    birthday: 'bday',
    address: 'street-address',
    city: 'locality',
    zip_code: 'postal-code',
  };
  @ViewChild('input', { static: true }) public input: ElementRef;
  @Input() public controlName: string;

  @Input()
  public set properties(properties: Schema) {
    this._properties = properties;
    this.inputType = this.getInputType();
    this.inputSteps = this.getInputSteps();
    this.required = this.isRequired();
  }

  public get properties(): Schema {
    return this._properties;
  }

  @Input() public schemaTranslations: SchemaTranslations | null;
  @Input() public errors: any;
  @Input() public readonly = false;
  public formControl = new UntypedFormControl(null);
  protected inputType: InputType;
  protected inputSteps: number | null;
  protected required: boolean;
  protected focused = false;

  public control = new FormControl();
  private formSub: Subscription;
  private _properties: Schema;

  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  public ngOnInit(): void {
    this.formSub = this.formControl.valueChanges.subscribe(() => this.formControlChange());
  }

  public ngOnDestroy(): void {
    this.formSub.unsubscribe();
  }

  public writeValue(obj: any): void {
    this.formControl.patchValue(obj);
    this.cdr.markForCheck();
  }

  public validate(): ValidationErrors | any {
    return this.formControl.errors;
  }

  public getInputType(): InputType {
    if (this.isNumber()) { return 'number'; }
    if (this.properties.isEmailField(this.controlName)) { return 'email'; }
    if (this.properties.isBooleanField(this.controlName)) { return 'checkbox'; }

    return 'text';
  }

  public getInputSteps(): number | null {
    return this.properties.getNumberSteps(this.controlName);
  }

  public focus(): void {
    this.input.nativeElement.focus();
  }

  public isRequired(): boolean {
    return this.properties.isFieldRequired(this.controlName) || this.properties.isEmailIdentifier() && this.controlName === IdentifierType.EMAIL;
  }

  private formControlChange(): void {
    this.onTouched();
    const formValue = this.isNumber() && this.formControl.value ? parseInt(this.formControl.value, 10) : this.formControl.value;
    this.onChange(formValue);
  }

  private isNumber(): boolean {
    return this.properties.hasNumericValues(this.controlName);
  }
}
